import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  /**
   * Plan slug the user signed up to. Null only between signup and the
   * first successful Stripe checkout (rare — the signup flow redirects
   * to checkout immediately).
   */
  plan: "solo" | "pro" | "label" | null;
  /**
   * Mirrored from Stripe's subscription. The dashboard layout reads this
   * to decide whether to show the SubscriptionBanner / lock state-changing
   * pages. Refreshed on /api/auth/me and /api/billing/subscription calls.
   */
  subscriptionStatus?:
    | "trialing"
    | "active"
    | "past_due"
    | "canceled"
    | "incomplete"
    | "incomplete_expired"
    | "unpaid"
    | null;
  /**
   * `admin` users see the /admin route in the sidebar and can access the
   * admin panel. Promotion is done through the `promote-admin.ts` CLI
   * script — there's intentionally no UI to grant admin access.
   */
  role?: "user" | "admin";
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  setSession: (token: string, user: AuthUser) => void;
  setUser: (user: AuthUser) => void;
  clear: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      setSession: (token, user) => set({ token, user }),
      setUser: (user) => set({ user }),
      clear: () => set({ token: null, user: null }),
    }),
    { name: "ib-auth" }
  )
);
